import { create } from "zustand";
import {
  Widget,
  WidgetTemplate,
  DashboardState,
  DashboardConfig,
  DashboardContent,
  MapViewState,
} from "../types/dashboard";
import { getDashboardData, resetDashboardData, saveDashboardData } from "../../../api/admin";
import { useAuthStore } from "../../../store/useAuthStore";

const defaultConfig: DashboardConfig = {
  gridColumns: 4,
  autoSave: true,
  snapToGrid: true,
};

const defaultContent: DashboardContent = {
  mainHeading: "OSMOAIR",
  pageTitle: "Dashboard",
  pageSubtitle: "XXXYYYZZ",
  description: "Monitor live data across your sites and devices.",
};

/**
 * The widget-library order configured on the Settings screen (`availableWidgets`)
 * is the single source of truth for how widgets are arranged on the dashboard.
 * Re-derive each active widget's `order` from its matching template position so
 * the dashboard always reflects the saved configuration — consistently across
 * every user, browser and device in the organization.
 */
const orderActiveWidgetsByTemplates = (
  widgets: Widget[],
  templates: WidgetTemplate[],
): Widget[] => {
  const templateOrder = (w: Widget) =>
    templates.find((t) => t.type === w.type && t.title === w.title)?.order ??
    Number.MAX_SAFE_INTEGER;
  return [...widgets]
    .sort((a, b) => templateOrder(a) - templateOrder(b))
    .map((w, i) => ({ ...w, order: i + 1 }));
};

const availableWidgets: WidgetTemplate[] = [
  {
    id: "grid-1",
    type: "grid",
    title: "Grid View",
    subtitle: "Card Based Live Data",
    color: "danger",
    defaultSize: "full",
    order: 1,
    description: "Display live sensor data",
  },
  {
    id: "table-1",
    type: "table",
    title: "Table UI",
    subtitle: "Data in tabular format",
    color: "primary",
    defaultSize: "full",
    order: 2,
    description: "Display data in organized rows and columns",
  },
  {
    id: "map-1",
    type: "map",
    title: "Map View",
    subtitle: "Geographic data visualization",
    color: "success",
    defaultSize: "full",
    order: 3,
    description: "View data plotted on interactive maps",
  },
  {
    id: "graph-1",
    type: "graph",
    title: "Graph",
    subtitle: "Chart and graph visualization",
    color: "warning",
    defaultSize: "full",
    order: 4,
    description: "Display data with various chart types",
  },
  {
    id: "report-1",
    type: "report",
    title: "Generate Report Card",
    subtitle: "Generate detailed reports",
    color: "info",
    defaultSize: "full",
    order: 5,
    description: "Create and download comprehensive sensor data reports",
  },
];

export const useDashboard = create<DashboardState>((set, get) => ({
  activeWidgets: [],
  availableWidgets,
  config: defaultConfig,
  content: defaultContent,
  isEditMode: false,
  isLoading: true,
  selectedParams: [],
  selectedDay: "7 Days",
  selectedTimeGap: "12 hour",
  deviceCoordinates: [],
  mapViewState: { scale: 1, translateX: 0, translateY: 0 },
  addWidget: (template: WidgetTemplate, deviceId: string) => {
    const { activeWidgets } = get();

    // Prevent adding a duplicate widget of the same type
    if (activeWidgets.some((w) => w.type === template.type)) {
      console.warn(`A ${template.type} widget already exists`);
      return;
    }

    const newWidget: Widget = {
      id: `${template.id}-${Date.now()}`,
      type: template.type,
      title: template.title,
      subtitle: template.subtitle,
      color: template.color,
      size: template.defaultSize,
      order: template.order,
      isVisible: true,
    };

    // Insert at the widget's configured position (from the Settings library
    // order) rather than appending to the bottom, so an enabled widget shows up
    // exactly where the configuration places it.
    set((state) => ({
      activeWidgets: orderActiveWidgetsByTemplates(
        [...state.activeWidgets, newWidget],
        state.availableWidgets,
      ),
    }));

    if (get().config.autoSave && deviceId && deviceId !== "default") {
      get().saveDashboard(deviceId);
    }
  },

  removeWidget: (id: string, deviceId: string) => {
    const { activeWidgets } = get();
    const widgetToRemove = activeWidgets.find((widget) => widget.id === id);

    // Prevent removal of Table UI widget
    if (widgetToRemove && widgetToRemove.type === "table" && widgetToRemove.title === "Table UI") {
      console.warn("Cannot remove the default Table UI widget");
      return;
    }

    set((state) => ({
      activeWidgets: state.activeWidgets.filter((widget) => widget.id !== id),
    }));

    if (get().config.autoSave && deviceId && deviceId !== "default") {
      get().saveDashboard(deviceId);
    }
  },

  updateWidget: (id: string, updates: Partial<Widget>, deviceId: string) => {
    set((state) => ({
      activeWidgets: state.activeWidgets.map((widget) =>
        widget.id === id ? { ...widget, ...updates } : widget
      ),
    }));

    if (get().config.autoSave && deviceId && deviceId !== "default") {
      get().saveDashboard(deviceId);
    }
  },

  updateContent: (updates: Partial<DashboardContent>, deviceId: string) => {
    set((state) => ({
      content: { ...state.content, ...updates },
    }));

    if (get().config.autoSave && deviceId && deviceId !== "default") {
      get().saveDashboard(deviceId);
    }
  },

  toggleEditMode: () => {
    set((state) => ({
      isEditMode: !state.isEditMode,
    }));
  },

  setEditMode: (isEditMode: boolean) => {
    set({ isEditMode });
  },

  reorderWidgets: (activeId: string, overId: string, deviceId: string) => {
    const { activeWidgets, availableWidgets } = get();
    const activeIndex = activeWidgets.findIndex((widget) => widget.id === activeId);
    const overIndex = activeWidgets.findIndex((widget) => widget.id === overId);

    if (activeIndex !== -1 && overIndex !== -1) {
      const newWidgets = [...activeWidgets];
      const [removed] = newWidgets.splice(activeIndex, 1);
      newWidgets.splice(overIndex, 0, removed);

      // Update order numbers for all widgets
      const updatedWidgets = newWidgets.map((widget, index) => ({
        ...widget,
        order: index + 1,
      }));

      // Mirror the new arrangement onto the template library so it becomes the
      // org-wide configuration and survives reload (loadDashboard derives the
      // dashboard order from the templates). Active templates take the dragged
      // order; inactive templates keep their existing slots.
      const keyOf = (w: { type: string; title: string }) => `${w.type}::${w.title}`;
      const reorderedActiveTemplates = updatedWidgets
        .map((w) => availableWidgets.find((t) => keyOf(t) === keyOf(w)))
        .filter((t): t is WidgetTemplate => Boolean(t));
      const activeKeys = new Set(reorderedActiveTemplates.map(keyOf));
      let ai = 0;
      const updatedTemplates = availableWidgets
        .map((t) => (activeKeys.has(keyOf(t)) ? reorderedActiveTemplates[ai++] : t))
        .map((t, index) => ({ ...t, order: index + 1 }));

      set({ activeWidgets: updatedWidgets, availableWidgets: updatedTemplates });

      if (get().config.autoSave) {
        get().saveDashboard(deviceId);
      }
    }
  },

  resetDashboard: async (selectedDeviceId: string) => {
    // Reset to empty widgets first
    set({ activeWidgets: [] });
    await resetDashboardData(selectedDeviceId).then(() => {
      // Load dashboard which will automatically add the Table UI widget
      get().loadDashboard(selectedDeviceId);
    });
  },

  saveDashboard: async (selectedDeviceId: string) => {
    // Dashboard data is stored per device; "default" is a placeholder, not a real device id
    if (!selectedDeviceId || selectedDeviceId === "default") return;
    // Dashboard config is shared org-wide; USERs are view-only and the backend
    // rejects their saves, so don't attempt them (local state still updates).
    const { role } = useAuthStore.getState();
    if (role !== "ADMIN" && role !== "SUPER_ADMIN") return;
    const {
      activeWidgets,
      config,
      content,
      availableWidgets,
      selectedDay,
      selectedParams,
      selectedTimeGap,
      deviceCoordinates,
      mapViewState,
    } = get();
    const dashboardData = {
      dashboardData: {
        activeWidgets,
        config,
        content,
        availableWidgets,
        selectedDay,
        selectedParams,
        selectedTimeGap,
        deviceCoordinates,
        mapViewState,
      },
    };
    await saveDashboardData(dashboardData, selectedDeviceId);
  },

  loadDashboard: async (selectedDeviceId: string) => {
    set({ isLoading: true });
    try {
      // If no real device is selected, initialize with default widgets without API call
      if (!selectedDeviceId || selectedDeviceId === "default") {
        const tableWidgetTemplate = get().availableWidgets.find((w) => w.id === "table-1");

        if (tableWidgetTemplate) {
          const defaultTableWidget: Widget = {
            id: `${tableWidgetTemplate.id}-${Date.now()}`,
            type: tableWidgetTemplate.type,
            title: tableWidgetTemplate.title,
            subtitle: tableWidgetTemplate.subtitle,
            color: tableWidgetTemplate.color,
            size: tableWidgetTemplate.defaultSize,
            order: 1,
            isVisible: true,
          };

          set({
            activeWidgets: [defaultTableWidget],
            config: defaultConfig,
            content: defaultContent,
            availableWidgets: get().availableWidgets,
            selectedParams: [],
            selectedDay: "7 Days",
            selectedTimeGap: "12 hour",
          });
        }
        return;
      }

      const savedData = await getDashboardData(selectedDeviceId);

      if (savedData) {
        try {
          const {
            activeWidgets,
            config,
            content,
            selectedDay,
            selectedParams,
            selectedTimeGap,
            deviceCoordinates,
            mapViewState: savedMapViewState,
          } = savedData;

          let widgetsToSet = activeWidgets || [];

          // Deduplicate widgets by type – keep only the first occurrence of each type
          const seenTypes = new Set<string>();
          widgetsToSet = widgetsToSet.filter((w: Widget) => {
            if (seenTypes.has(w.type)) return false;
            seenTypes.add(w.type);
            return true;
          });

          // The Table UI widget is mandatory — if saved data is missing it
          // (e.g. removed by an older app version), restore it without
          // disturbing the saved order of the other widgets
          if (!widgetsToSet.some((w: Widget) => w.type === "table")) {
            const tableWidgetTemplate = get().availableWidgets.find((w) => w.id === "table-1");

            if (tableWidgetTemplate) {
              const defaultTableWidget: Widget = {
                id: `${tableWidgetTemplate.id}-${Date.now()}`,
                type: tableWidgetTemplate.type,
                title: tableWidgetTemplate.title,
                subtitle: tableWidgetTemplate.subtitle,
                color: tableWidgetTemplate.color,
                size: tableWidgetTemplate.defaultSize,
                order: tableWidgetTemplate.order,
                isVisible: true,
              };
              // Order all widgets by the widget-library order configured in
              // the settings screen (saved templates, falling back to defaults)
              const templates =
                ((savedData as any).availableWidgets as WidgetTemplate[] | undefined) ??
                get().availableWidgets;
              const orderOf = (w: Widget) =>
                templates.find((t) => t.type === w.type && t.title === w.title)?.order ??
                Number.MAX_SAFE_INTEGER;
              widgetsToSet = [...widgetsToSet, defaultTableWidget]
                .sort((a: Widget, b: Widget) => orderOf(a) - orderOf(b))
                .map((w: Widget, i: number) => ({ ...w, order: i + 1 }));
            }
          }

          // Restore saved widget order if available, but always use current template definitions
          const currentTemplates = get().availableWidgets;
          const savedTemplates = (savedData as any).availableWidgets as WidgetTemplate[] | undefined;
          let mergedWidgets = currentTemplates;

          if (savedTemplates && savedTemplates.length > 0) {
            // Use saved order but only for templates that still exist in current definitions
            const seenIds = new Set<string>();
            const ordered: WidgetTemplate[] = [];

            // First, add templates in saved order (using current template data)
            for (const saved of savedTemplates) {
              const current = currentTemplates.find((t) => t.id === saved.id);
              if (current && !seenIds.has(current.id)) {
                seenIds.add(current.id);
                ordered.push(current);
              }
            }

            // Then, append any new templates not in saved data
            for (const current of currentTemplates) {
              if (!seenIds.has(current.id)) {
                seenIds.add(current.id);
                ordered.push(current);
              }
            }

            // Re-assign order numbers
            mergedWidgets = ordered.map((w, i) => ({ ...w, order: i + 1 }));
          }

          // The configured template order is authoritative: align the active
          // widgets to it so the dashboard renders in the arrangement saved on
          // the Settings screen, regardless of any stale per-widget order.
          widgetsToSet = orderActiveWidgetsByTemplates(widgetsToSet, mergedWidgets);

          set({
            activeWidgets: widgetsToSet,
            config: { ...defaultConfig, ...config },
            content: { ...defaultContent, ...content },
            availableWidgets: mergedWidgets,
            selectedParams,
            selectedDay,
            selectedTimeGap,
            deviceCoordinates,
            mapViewState: savedMapViewState || { scale: 1, translateX: 0, translateY: 0 },
          });
        } catch (error) {
          console.error("Failed to load dashboard:", error);
        }
      } else {
        // If no saved data exists, add the default Table UI widget
        const tableWidgetTemplate = get().availableWidgets.find((w) => w.id === "table-1");

        if (tableWidgetTemplate) {
          const defaultTableWidget: Widget = {
            id: `${tableWidgetTemplate.id}-${Date.now()}`,
            type: tableWidgetTemplate.type,
            title: tableWidgetTemplate.title,
            subtitle: tableWidgetTemplate.subtitle,
            color: tableWidgetTemplate.color,
            size: tableWidgetTemplate.defaultSize,
            order: 1,
            isVisible: true,
          };

          set({
            activeWidgets: [defaultTableWidget],
            config: defaultConfig,
            content: defaultContent,
            availableWidgets: get().availableWidgets,
            selectedParams: [],
            selectedDay: "7 Days",
            selectedTimeGap: "12 hour",
          });
        }
      }
    } finally {
      set({ isLoading: false });
    }
  },

  reorderWidgetTemplates: (activeId: string, overId: string, selectedDeviceId: string) => {
    const { availableWidgets, activeWidgets } = get();
    const activeIndex = availableWidgets.findIndex((widget) => widget.id === activeId);
    const overIndex = availableWidgets.findIndex((widget) => widget.id === overId);

    if (activeIndex !== -1 && overIndex !== -1) {
      const newWidgets = [...availableWidgets];
      const [removed] = newWidgets.splice(activeIndex, 1);
      newWidgets.splice(overIndex, 0, removed);

      // Update order numbers for all widgets
      const updatedWidgets = newWidgets.map((widget, index) => ({
        ...widget,
        order: index + 1,
      }));

      // Also update the order of active widgets to match the new template order
      const updatedActiveWidgets = activeWidgets.map((activeWidget) => {
        const correspondingTemplate = updatedWidgets.find(
          (template) => template.type === activeWidget.type && template.title === activeWidget.title
        );

        if (correspondingTemplate) {
          return {
            ...activeWidget,
            order: correspondingTemplate.order,
          };
        }

        return activeWidget;
      });

      const sortedActiveWidgets = [...updatedActiveWidgets].sort((a, b) => a.order - b.order);

      // Single atomic update to prevent intermediate re-renders causing duplication
      set({ availableWidgets: updatedWidgets, activeWidgets: sortedActiveWidgets });

      if (get().config.autoSave) {
        get().saveDashboard(selectedDeviceId);
      }
    }
  },

  moveWidgetTemplateToTop: (widgetId: string, selectedDeviceId: string) => {
    const { availableWidgets, activeWidgets } = get();
    const widgetIndex = availableWidgets.findIndex((widget) => widget.id === widgetId);

    if (widgetIndex > 0) {
      const newWidgets = [...availableWidgets];
      const [removed] = newWidgets.splice(widgetIndex, 1);
      newWidgets.unshift(removed);

      const updatedWidgets = newWidgets.map((widget, index) => ({
        ...widget,
        order: index + 1,
      }));

      const updatedActiveWidgets = activeWidgets.map((activeWidget) => {
        const correspondingTemplate = updatedWidgets.find(
          (template) => template.type === activeWidget.type && template.title === activeWidget.title
        );

        if (correspondingTemplate) {
          return {
            ...activeWidget,
            order: correspondingTemplate.order,
          };
        }

        return activeWidget;
      });

      const sortedActiveWidgets = [...updatedActiveWidgets].sort((a, b) => a.order - b.order);

      set({ availableWidgets: updatedWidgets, activeWidgets: sortedActiveWidgets });

      if (get().config.autoSave) {
        get().saveDashboard(selectedDeviceId);
      }
    }
  },

  moveWidgetTemplateToBottom: (widgetId: string, selectedDeviceId: string) => {
    const { availableWidgets, activeWidgets } = get();
    const widgetIndex = availableWidgets.findIndex((widget) => widget.id === widgetId);

    if (widgetIndex !== -1 && widgetIndex < availableWidgets.length - 1) {
      const newWidgets = [...availableWidgets];
      const [removed] = newWidgets.splice(widgetIndex, 1);
      newWidgets.push(removed);

      const updatedWidgets = newWidgets.map((widget, index) => ({
        ...widget,
        order: index + 1,
      }));

      const updatedActiveWidgets = activeWidgets.map((activeWidget) => {
        const correspondingTemplate = updatedWidgets.find(
          (template) => template.type === activeWidget.type && template.title === activeWidget.title
        );

        if (correspondingTemplate) {
          return {
            ...activeWidget,
            order: correspondingTemplate.order,
          };
        }

        return activeWidget;
      });

      const sortedActiveWidgets = [...updatedActiveWidgets].sort((a, b) => a.order - b.order);

      set({ availableWidgets: updatedWidgets, activeWidgets: sortedActiveWidgets });

      if (get().config.autoSave) {
        get().saveDashboard(selectedDeviceId);
      }
    }
  },
  setSelectedDay(day, selectedDeviceId) {
    set({ selectedDay: day });
    get().saveDashboard(selectedDeviceId);
  },
  setSelectedParams(param, selectedDeviceId) {
    const { selectedParams, saveDashboard } = get();
    const newParams = selectedParams.includes(param)
      ? selectedParams.filter((sp) => sp !== param)
      : [...selectedParams, param];
    set({ selectedParams: newParams });
    saveDashboard(selectedDeviceId);
  },
  setAllSelectedParams(params, selectedDeviceId) {
    set({ selectedParams: params });
    get().saveDashboard(selectedDeviceId);
  },
  setSelectedTimeGap(timeGap, selectedDeviceId) {
    set({ selectedTimeGap: timeGap });
    get().saveDashboard(selectedDeviceId);
  },
  saveDeviceCoordinates(deviceCoordinates, selectedDeviceId) {
    set({ deviceCoordinates });
    get().saveDashboard(selectedDeviceId);
  },
  setDeviceCoordinatesOnly(deviceCoordinates) {
    set({ deviceCoordinates });
  },
  saveMapViewState(mapViewState: MapViewState, selectedDeviceId: string) {
    set({ mapViewState });
    get().saveDashboard(selectedDeviceId);
  },
}));
